import axios from "axios";
import router from "../../../spa/src/router/router.js";
import { get, some, find } from "lodash";

/**
 * The Store Pattern
 * actions -> mutations
 * Never mutate the store directly.
 */

// initial state
const state = {
  loaded: false,
  orderNote: "",
  commerceItems: [],
  commerceItemCount: 0,
  totalCommerceItemCount: 0,
  isCommittingOrder: false,
  isFetchingCart: false,
  contactInfo: {
    email: "",
    firstName: "",
    lastName: "",
    nickname: "",
    phoneNumber: ""
  },
  useFullCart: false,
  taxExempt: false,
  bestAvailableDisclaimer: '',
  payAtPickupOrder: false
};

// getters
const getters = {
  cartItems: state => {
    return state.commerceItems;
  },
  cartSubstitutions: state => {
    return state.substitutionsAllowAll;
  },
  cartSubstitutionsEnabled: state => state.substitutionPreferenceEnabled,
  cartSubstitutionsDisclaimer: state => state.substitutionPreferenceDisclaimer,
  cartContainsAlcohol: state => get(state, ["containsAlcohol"], false),
  selectedEbtCard: state => {
    const paymentGroups = get(state, "paymentGroups", []);
    return find(paymentGroups, (payment) => payment.paymentMethod === "ebtSnapPaymentGroup" && !!payment.paymentAlias);
  },
  selectedCreditCard: state => {
    const paymentGroups = get(state, "paymentGroups", []);
    return find(paymentGroups, (payment) => payment.paymentMethod === "creditCard");
  },
  hasValidCreditCardSelected: (state, getters) => !!getters.selectedCreditCard.paymentAlias,
  hasValidPayment: (state, getters) => {
    const paymentGroups = get(state, "paymentGroups", []);
    // If there's a gift card on the order, make sure there is also a credit card
    if (some(paymentGroups, 'giftCardPaymentGroup')) {
      return getters.hasValidCreditCardSelected;
    }
    // Otherwise, any card with a payment alias will do (currently that's credit/debit/EBT)
    return some(paymentGroups, (payment) => !!payment.paymentAlias);
  },
  ageVerificationRequired: state => get(state, 'ageVerificationRequired', false),
  ageVerificationCompleted: state => get(state, 'ageVerificationCompleted', false),
};

// actions - can be async
const actions = {
  // Load the cart, either full or estimated. Full should only be used on checkout page since it includes costly tax calculation
  LOAD_CART({ COMMIT, state }, options) {
    var vm = this;

    if (!options || !options.skipClearErrors) {
      vm.commit("errors/CLEAR_ERRORS");
    }
    vm.commit("cart/SET_FETCHING_CART", true);

    axios
      .all([
        axios.get(
          state.useFullCart
            ? "/commerce-api/v1/cart/full"
            : "/commerce-api/v1/cart/estimated"
        )
      ])
      .then(
        axios.spread(response => {
          // console.log('~ LOAD_CART:', response.data)
          const { data } = response;
          vm.commit("cart/SET_CART_DATA", data);
          vm.commit("reservation/CREATE_RESERVATION", data);
          vm.commit("reservation/UPDATE_TIMESLOT", data);
          vm.commit("reservation/UPDATE_EXPIRATION", data.currentTime);
          vm.commit("stores/SET_MY_STORE", data);
        })
      )
      .catch(error => {
        vm.dispatch("errors/HANDLE_ERROR", error);
      })
      .finally(() => {
        vm.commit("cart/SET_FETCHING_CART", false);
      });
  },

  POST_GRATUITY_AMOUNT({ COMMIT }, body) {
    var vm = this;

    /**
     * If the user entered a custom tip
     * {
     *   type: "AMOUNT", //optional
     *   amount: 1.50
     * }
     *
     * If the user chose a percentage, 5% 10% 15%
     * {
     *   type: "PERCENTAGE",
     *   amount: 10
     * }
     *
     *  */

    // Make sure it's a number
    body.amount = body.amount * 1;

    vm.commit("errors/CLEAR_ERRORS");
    // if (body.amount >= 1000) {
    //   return
    // }

    // var body = {
    //   amount: parseFloat(gratuityAmount)
    // }

    axios
      .all([axios.post("/commerce-api/v1/cart/tip", body)])
      .then(() => vm.dispatch("cart/LOAD_CART"))
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  POST_PROMO_CODE({ COMMIT, state }, promoCode) {
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");

    var body = {
      promoCode: promoCode.promoCode.toUpperCase()
    };

    axios
      .all([axios.post("/commerce-api/v1/cart/promo", body)])
      .then(
        axios.spread(response => {
          // /promo only returns an estimated cart, so if we need the full cart we have to refetch it.
          // We don't need to waste another call if we do want estimated, though.
          if (state.useFullCart) {
            vm.dispatch("cart/LOAD_CART");
          } else {
            vm.commit("cart/SET_CART_DATA", response.data);
          }
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status,
            type: "promoCode"
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", {
            message: error.message,
            type: "promoCode"
          });
        }
        console.log(error.config);
      });
  },
  DELETE_PROMO_CODE({ COMMIT, state }, promoCode) {
    var vm = this;

    axios
      .all([axios.delete("/commerce-api/v1/cart/promo/" + promoCode)])
      .then(
        axios.spread(response => {
          // /promo only returns an estimated cart, so if we need the full cart we have to refetch it.
          // We don't need to waste another call if we do want estimated, though.
          if (state.useFullCart) {
            vm.dispatch("cart/LOAD_CART");
          } else {
            vm.commit("cart/SET_CART_DATA", response.data);
          }
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  SET_QUANTITY({ COMMIT }, item) {
    var vm = this;

    // var body = {
    //   productId: productId,
    //   skuId: 123,
    //   quantity: 8
    // }

    axios
      .all([axios.post("/commerce-api/v1/cart/item", item)])
      .then(
        axios.spread(response => {
          vm.commit("cart/SET_CART_DATA", response.data);
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  DELETE_ITEM({ COMMIT }, id) {
    var vm = this;
    axios
      .all([axios.delete("/commerce-api/v1/cart/item/" + id)])
      .then(
        axios.spread(response => {
          // This call is not returning the full cart object back. Why?
          vm.commit("cart/SET_CART_DATA", response.data);
        })
      )
      .then(() => {
        // After an item gets deleted and you are on the coupon detail modal, the DOM is removing modal-open from the body
        // this will make sure that the modal-open stays on the body and modal will work as is
        // I have not been unable to find the root issue as to why the DOM is removing modal-open but this will help with the issue
        let checkifModalIsOpen =
          document.querySelectorAll(".modal.show").length > 0;
        let $body = document.body;

        if (checkifModalIsOpen && !$body.classList.contains("modal-open")) {
          $body.classList.add("modal-open");
        }
      })
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  DELETE_CART({ COMMIT }, id) {
    var vm = this;
    axios
      .all([axios.delete("/commerce-api/v1/cart")])
      .then(
        axios.spread(response => {
          vm.commit("cart/SET_CART_DATA", response.data);
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  ADD_ITEM_TO_BUCKET({ COMMIT }, item) {
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");

    /**
     * Adds an item to the cart. The `item` object should look like this
     * item: {
     *   productId: '6456',
     *   skuId: '61678',
     *   quantity: 1,
     *   note: ''
     * }
     */

    return axios
      .all([axios.post("/commerce-api/v1/cart/item", item)])
      .then(
        axios.spread(response => {
          vm.commit("cart/SET_CART_DATA", response.data);
          return response;
        })
      )
      .catch(error => {
          // Custom error handling for immediacy:
          if (_.get(error, "response.data.errors[0]['o:errorCode']", false) === "error.maximumImmediacyOrderSize") {
            window.__EXORDX__.dispatch({
                type: "@@HEB/MODALS/SHOW_MODAL",
                payload: {
                    modal: "CART_ERROR_MODAL",
                    props: {
                        title: 'Cart limit reached',
                        message: error.response.data.errors[0].title,
                        confirmLabel: 'Change time'        
                    }  
                },
            });

            return { status: error.response.status, data: { errors: [] } }; // Return empty error since the modal shows the message
        }
        if (_.get(error, "response.data.errors[0]['o:errorCode']", false) === "itemUnavailableDueToImmediacy") {
            window.__EXORDX__.dispatch({
                type: "@@HEB/MODALS/SHOW_MODAL",
                payload: {
                    modal: "CART_ERROR_MODAL",
                    props: {
                        title: 'Change time?',
                        message: error.response.data.errors[0].title,
                        confirmLabel: 'Change time'        
                    }  
                },
            });

            return { status: error.response.status, data: { errors: [] } }; // Return empty error since the modal shows the message
        }

        // Special Case: Swallow maximumOrderQuantity since it is displayed inline
        if (
          _.get(error, "response.data.errors[0]['o:errorCode']", false) ===
          "error.maximumOrderQuantity"
        ) {
          return error.response;
        }

        // Error
        if (error.response) {
          const msg = error.response.data.title;
          vm.commit("errors/PUSH_ERROR", {
            message: msg,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        return error.response;
      });
  },
  PUT_ITEM_QUANTITY({ COMMIT }, item) {
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");
    //  @Endpoint(id="/cart/item/{commerceItemId}/quantity#PUT"

    return axios
      .all([
        axios.put("/commerce-api/v1/cart/item/" + item.id + "/quantity", {
          quantity: item.quantity
        })
      ])
      .then(
        axios.spread(response => {
          vm.commit("cart/SET_CART_DATA", response.data);
          return response;
        })
      )
      .catch(error => {
        // Custom error handling for immediacy:
        if (_.get(error, "response.data.errors[0]['o:errorCode']", false) === "error.maximumImmediacyOrderSize") {
            window.__EXORDX__.dispatch({
                type: "@@HEB/MODALS/SHOW_MODAL",
                payload: {
                    modal: "CART_ERROR_MODAL",
                    props: {
                        title: 'Cart limit reached',
                        message: error.response.data.errors[0].title,
                        confirmLabel: 'Change time'        
                    }  
                },
            });

            return { status: error.response.status, data: { errors: [] } }; // Return empty error since the modal shows the message
          }
        // Special Case: Swallow maximumOrderQuantity since it is displayed inline
        if (
          _.get(error, "response.data.errors[0]['o:errorCode']", false) ===
          "error.maximumOrderQuantity"
        ) {
          return error.response;
        }

        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }

        return error.response;
      });
  },
  PUT_ITEM_NOTE({ COMMIT, state }, item) {
    var vm = this;

    // //Remove any non-ASCII Characters before sending to API
    // const noteSanitized = item.note.note.substring(0, 255).replace(/[^\x00-\x7F]/g, "");

    axios
      .all([
        axios.put("/commerce-api/v1/cart/item/" + item.id + "/note", {
          note: item.note.note.substring(0, 255)
        })
      ])
      .then(
        axios.spread(response => {
          vm.commit("cart/SET_CART_DATA", response.data);
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  POST_ORDER_NOTE({ COMMIT }, note) {
    var vm = this;

    //Remove any non-ASCII Characters before sending to API
    // const noteSanitized = note.replace(/[^\x00-\x7F]/g, "");

    axios
      .all([axios.post("/commerce-api/v1/cart/note", { note })])
      .then(
        axios.spread(response => {
          vm.commit("cart/EDIT_ORDER_NOTE", response.data.orderNote);
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  POST_DELIVERY_INSTRUCTIONS({ COMMIT }, instructions) {
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");

    axios
      .all([axios.post("/commerce-api/v1/cart/instructions", instructions)])
      .then(
        axios.spread(response => {
          vm.commit("cart/EDIT_DELIVERY_INSTRUCTIONS", {
            instructions: response.data.deliveryInstructions,
            location: response.data.preferredDeliveryLocation
          });
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          const isLeaveAtDoorErr =
            get(error, "response.data.errors[0]['o:errorCode']") ===
            "leaveAtDoorUnavailableDueToAlcohol";
          // COVID: Msg should be switched to the following when leave-at-door is no longer mandatory:
          // "Orders with alcohol can't be left at the door. Please remove those items or change your delivery option."
          const leaveAtDoorMsg =
            "As a precaution, all orders are being left at the door. Because alcohol requires ID verification, delivery orders can't include alcohol.";
          const msg = isLeaveAtDoorErr
            ? leaveAtDoorMsg
            : get(error.response, "data.title");
          vm.commit("errors/PUSH_ERROR", {
            message: msg,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  PUT_ITEM_SUBSTITUTION({ COMMIT, state }, item) {
    var vm = this;

    // @endpoint {{URL}}/commerce-api/v1/cart/item/ci1054000001/substitution",

    axios
      .all([
        axios.put("/commerce-api/v1/cart/item/" + item.id + "/substitution", {
          allowed: item.allowSubstitution
        })
      ])
      .then(
        axios.spread(response => {
          vm.commit("cart/SET_CART_DATA", response.data);
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  PUT_CART_SUBSTITUTION({ COMMIT, state }, boolean) {
    var vm = this;

    // @endpoint {{URL}}/commerce-api/v1/cart/substitution",
    axios
      .all([
        axios.put("/commerce-api/v1/cart/substitution", { allowed: boolean })
      ])
      .then(
        axios.spread(response => {
          vm.commit("cart/SET_CART_DATA", response.data);
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  POST_GIFT_CARD({ COMMIT, state }, obj) {
    var vm = this;

    var body = {
      giftCardNumber: obj.giftcard.number,
      giftCardPinNumber: obj.giftcard.pin,
      paymentAlias: obj.activeCardAlias
    };
    vm.commit("errors/CLEAR_ERRORS");

    axios
      .all([axios.post("/commerce-api/v1/checkout/giftCard", body)])
      .then(() => {
        // /giftCard only returns an estimated cart. We always have to re-fetch the full cart since this is only called from checkout.
        vm.dispatch("cart/LOAD_CART");

        // Success Message, not actually an error
        vm.commit("errors/PUSH_ERROR", {
          message: "Gift card applied! View updated total in the order summary",
          type: "giftCard",
          icon: "fa-check",
          variant: "success"
        });
        obj.giftcard.vm.hideAndClearGiftCardInput();
        obj.giftcard.vm.giftcard.isLoading = false;
      })
      .catch(error => {
        // Error
        obj.giftcard.vm.giftcard.isLoading = false;

        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status,
            type: "giftCard"
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", {
            message: error.message,
            type: "giftCard"
          });
        }
        console.log(error.config);
      });
  },
  DELETE_GIFT_CARD({ COMMIT, state }, paymentGroupId) {
    var vm = this;

    vm.commit("errors/CLEAR_ERRORS");

    axios
      .all([
        axios.delete("/commerce-api/v1/checkout/giftCard/" + paymentGroupId)
      ])
      .then(
        // /giftCard only returns an estimated cart. We always have to re-fetch the full cart since this is only called from checkout.
        () => vm.dispatch("cart/LOAD_CART")
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status,
            type: "giftCard"
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", {
            message: error.message,
            type: "giftCard"
          });
        }
        console.log(error.config);
      });
  },
  POST_CONTACT_INFO({ COMMIT }, body) {
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");

    if (!body.phoneNumber) {
      return;
    }

    // Looks like this. All are optional.
    // body = {
    //   "firstName" : "John",
    //   "lastName" : "Galt",
    //   "email": "who_is_john_galt@atlas-shrugged.com",
    //   "phoneNumber": "555-555-5555"
    // }

    axios
      .all([axios.post("/commerce-api/v1/cart/contact", body)])
      .then(
        // /contact only returns an estimated cart. We always have to re-fetch the full cart since this is only called from checkout.
        () => vm.dispatch("cart/LOAD_CART")
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  FETCH_COUPON_DETAILS({ COMMIT }, coupon) {
    /**
     * This is used for the non-vue coupon card on Category/search pages as well as vue app
     * @param {object} coupon Contains information about the coupon to request
     * @param {boolean} summary Should the response omit buy-products and reward-products
     * summary = false will return buy-products and reward-products
     * summary = true is more performant and will not return buy-products and reward-products
     * @returns {object}
     *
     * @example
     * VueStore.$store.dispatch('cart/FETCH_COUPON_DETAILS', {
     *   id: 173961,
     *   callback: myFunctionName,
     *   summary: true
     * })
     *
     *
     */

    var vm = this;
    coupon.summary = coupon.summary || false;

    axios
      .all([
        axios.get(
          "/commerce-api/v1/coupon/details/" +
            coupon.id +
            "?summary=" +
            coupon.summary
        )
      ])
      .then(
        axios.spread(response => {
          // Make sure the callback is a function
          if (typeof coupon.callback === "function") {
            // Call it, since we have confirmed it is callable
            coupon.callback(response.data);
          }
        })
      )
      .catch(error => {
        // Error, fail silently (for Category Detail Page) HUP-3285
        console.log("Error loading coupon data:", error);

        // Error, Fail loudly for Coupon Detail Modal HUP-3508
        // This continues to execute callbacks even when there is an error retreiving coupon details
        // because of the ask to hide out-of-sync data on Search Results Page/Category Page (404)
        // and to show "Oops! this coupon is no longer available" on the Coupon detail modal instead
        // of the error text supplied in the JSON.
        if (typeof coupon.callback === "function") {
          coupon.callback({ errors: [error] });
        }
      });
  },
  CLIP_COUPON({ COMMIT }, coupon) {
    var vm = this;

    return axios
      .all([axios.post("/commerce-api/v1/coupon/clip/" + coupon.couponId)])
      .then(
        axios.spread(response => {
          // Refresh the cart, pricing
          if (
            window.heb.data.user.isLoggedIn ||
            window.heb.data.user.isRecognized
          ) {
            vm.dispatch("cart/LOAD_CART");
            vm.commit("profile/PUSH_CLIPPED_COUPON_ID", coupon.couponId);
            // Make sure the callback is a function
            if (typeof coupon.callback === "function") {
              // Call it, since we have confirmed it is callable
              coupon.callback(response.data);
            }

            return response
          } else {
            window.location.href =
              "/my-account/coupon-register-login?couponId=" + coupon.couponId;
          }
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
      });
  },
  ADD_ITEM_TO_LIST({ COMMIT }, { item, listName }) {
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");

    // var item = {
    //   productId: productId,
    //   skuId: skuId,
    //   listId: listId,
    //   unknownItemId: unknownItemId
    // }

    var queryString = Object.keys(item)
      .map(key => key + "=" + item[key])
      .join("&");

    axios
      .all([
        axios.get(
          "/mylist/includes/addToList.jsp?" + queryString + "&pageType=pdp"
        )
      ])
      .then(
        axios.spread(response => {
          console.log(response);
        })
      )
      .catch(error => {
        // Error
        if (error.response) {
          vm.commit("errors/PUSH_ERROR", {
            message: error.response.data.title,
            code: error.response.status,
            type: "promoCode"
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", {
            message: error.message,
            type: "promoCode"
          });
        }
        console.log(error.config);
      });
  },

  POST_ORDER({ COMMIT }, body) {
    // This function commits the order. All frontend checks should have been done by now.
    var vm = this;
    vm.commit("errors/CLEAR_ERRORS");
    vm.commit("cart/SET_COMMITTING_ORDER", true);

    // The body should look like this
    // Using a saved Credit Card
    // body = {
    //   'creditCardSource': 'SAVED',
    //   'ageVerified': 'false',
    //   'taxExempt': 'false',
    //   'primaryPaymentMethod': 'creditCard' || 'payAtPickup'
    // }

    // Using a new Credit Card
    // {
    //   "creditCardSource":"NEW",
    //   "saveCreditCard":"false",
    //   "ageVerified":"false",
    //   "taxExempt":"false"
    // }

    axios
      .all([axios.post("/commerce-api/v1/checkout/commit", body)])
      .then(
        axios.spread(response => {
          // Send user to thank you page
          vm.commit("cart/SET_CART_DATA", response.data);
          vm.commit("cart/SET_CHECKOUT_SUCCESS", true);
          router.push({ path: "/orderconfirmation" });

          // show an empty minicart
          const cartButton = document.querySelectorAll(
            ".btn-my-cart__item-count"
          )[0];
          if (cartButton) {
            cartButton.innerHTML = "";
            // document.querySelectorAll('.header__order-total')[0].innerHTML = '$0.00'
          }
        })
      )
      .catch(error => {
        // Error
        if (
          get(error, "response.data.errors[0]['o:errorCode']", false) ===
          "ebtDebitFailedDeclinedInsufFunds"
        ) {
          // This error updates the SNAP amount applied to the order,
          // so we need to refetch the cart to show the new value
          vm.dispatch("cart/LOAD_CART", { skipClearErrors: true });
        }

        if (error.response) {
          const isLeaveAtDoorErr =
            get(error, "response.data.errors[0]['o:errorCode']") ===
            "leaveAtDoorUnavailableDueToAlcohol";
          // COVID: Msg should be switched to the following when leave-at-door is no longer mandatory:
          // "Orders with alcohol can't be left at the door. Please remove those items or change your delivery option."
          const leaveAtDoorMsg =
            "As a precaution, all orders are being left at the door. Because alcohol requires ID verification, delivery orders can't include alcohol.";
          const msg = isLeaveAtDoorErr
            ? leaveAtDoorMsg
            : get(error.response, "data.title");
          vm.commit("errors/PUSH_ERROR", {
            message: msg,
            code: error.response.status
          });
        } else if (error.request) {
          vm.commit("errors/PUSH_ERROR", {
            message: "Couldn't contact the website"
          });
          console.log(error.request);
        } else {
          console.log("Error", error.message);
          vm.commit("errors/PUSH_ERROR", { message: error.message });
        }
        console.log(error.config);
        vm.commit("cart/SET_COMMITTING_ORDER", false);
      });
  },
  TOGGLE_TAX_EXEMPTION({ COMMIT, state }) {
    axios
      .post(
        `/commerce-api/v1/cart/taxExempt?context=curbside&taxExempt=${!state.taxExempt}`
      )
      .then(response => {
        this.commit("cart/SET_CART_DATA", response.data);
      })
      .catch(error => {
        this.commit("errors/PUSH_ERROR", {
          message: _.get(error.response, "data.title", error.message),
          code: _.get(error.response, "status")
        });
      });
  },
  SET_CARD_SELECTION({ COMMIT }, { paymentAlias, paymentType, selected }) {
    // paymentType is only needed if an EBT card is being toggled to determine if it's ebtSnap or ebtTanf
    let body = {
      paymentAlias,
      selected,
      ...(paymentType ? { paymentType } : {})
    };
    this.commit("errors/CLEAR_ERROR_TYPE", "paymentMethods");

    axios.post('/commerce-api/v1/checkout/payment/selection', body)
      .then(response => {
        this.commit("cart/SET_CART_DATA", response.data);
      })
      .catch((error) => {
        this.commit("errors/PUSH_ERROR", {
          type: "paymentMethods",
          message: get(error.response, "data.title", error.message),
          code: get(error.response, "status")
        });
      });
    },
    EDIT_EBT_AMOUNT({ COMMIT }, { paymentAlias, amount, paymentType = 'ebtSnap' }) {
      let body = {
        paymentAlias,
        amount,
        paymentType
      };
      this.commit("errors/CLEAR_ERROR_TYPE", "paymentMethods");

      return axios.post('/commerce-api/v1/checkout/payment/amount', body)
        .then(response => {
          this.commit("cart/SET_CART_DATA", response.data);
        })
        .catch((error) => {
          this.commit("errors/PUSH_ERROR", {
            type: "paymentMethods",
            message: get(error.response, "data.title", error.message),
            code: get(error.response, "status")
          });
        })
    },
};

// mutations - Must be synchronous
const mutations = {
  SET_CART_DATA(state, data) {
    // Default
    data.substitutionsAllowAll = true;
    data.isCommittingOrder = false;

    // These fields don't make sense to be overwritten by the cart response data. They should live separately from this cart
    // response data, but making that change would require a lot of refactoring.
    data.isFetchingCart = state.isFetchingCart;
    data.useFullCart = state.useFullCart;
    data.payAtPickupOrder = state.payAtPickupOrder;

    data.orderNote = data.orderNote || "";
    data.deliveryInstructions = data.deliveryInstructions || "";
    data.preferredDeliveryLocation = data.preferredDeliveryLocation || "";
    // Transform/add any frontend field we need

    // add all products to object, to lookup by key of product id
    const itemsById = {};

    data.commerceItems.forEach(function(item) {
      itemsById[item.id] = item;
    });

    data.itemsById = itemsById;

    // loop through each item in the cart
    // Add isDeleting property to each commerceItem, used to confirm delete item
    // Each item will be put into it's own L1 category
    var categoriesWithItems = JSON.parse(
      JSON.stringify(window.heb.data.general.l1)
    );
    categoriesWithItems.forEach(function(category) {
      category.items = [];
    });

    data.commerceItems.forEach(function(item) {
      // If note object doesn't exist, create one for vue
      item.note = {
        note: item.itemNote || "",
        isEditing: false
      };

      // frontend isDiscounted flag
      if (
        item.priceInfo.totalAmount.amount !==
        item.priceInfo.rawTotalPrice.amount
      ) {
        item.priceInfo.isDiscounted = true;
      } else {
        item.priceInfo.isDiscounted = false;
      }

      // Set item level substitution. Default is true (allowed)
      item.allowSubstitution = item.allowSubstitution !== false;

      if (item.substitutionPreferenceEnabled && item.allowSubstitution === false) {
        data.substitutionsAllowAll = false;
      }

      // put the item in it's category
      const itemCategoryName = item.product.defaultLevelOneCategory.name;
      const itemCategoryId =
        item.product.defaultLevelOneCategory.id || itemCategoryName;
      let categoryObj = categoriesWithItems.find(
        category => category.id == itemCategoryId
      );

      if (!categoryObj) {
        // category doesn't exist so make it
        categoriesWithItems.push({
          id: itemCategoryId,
          shortDisplayName: itemCategoryName,
          displayName: itemCategoryName,
          items: [item]
        });
      } else {
        categoryObj.items.push(item);
      }
    });

    data.commerceItems = categoriesWithItems;

    const usingEbt = some(
      data.paymentGroups,
      (payment) => payment.paymentMethod === "ebtSnapPaymentGroup" && !!payment.paymentAlias
    );
    if (usingEbt) {
      // If they've selected an EBT card, sort the cart items by SNAP eligible/ineligible.
      // This is done after the normal category sort so that it retains the normal
      // category order within the new parent categories.
      const snapItems = [];
      const nonSnapItems = [];
      const ebtSubtotal = get(data, "priceInfo.ebtSnapSubtotal.formattedAmount", "");
      const nonEbtSubtotal = get(data, "priceInfo.nonEbtSnapItemSubtotal.formattedAmount", "");

      categoriesWithItems.forEach(category =>
        category.items.forEach(item => item.product.isEbtSnapProduct ? snapItems.push(item) : nonSnapItems.push(item))
      );

      data.commerceItems = [
        {
          id: 'snapEligible',
          shortDisplayName: `SNAP eligible${ebtSubtotal && ` (${ebtSubtotal})`}`,
          displayName: `SNAP eligible${ebtSubtotal && ` (${ebtSubtotal})`}`,
          items: snapItems,
        }, {
          id: 'snapIneligible',
          shortDisplayName: `SNAP ineligible${nonEbtSubtotal && ` (${nonEbtSubtotal})`}`,
          displayName: `SNAP ineligible${nonEbtSubtotal && ` (${nonEbtSubtotal})`}`,
          items: nonSnapItems,
        }
      ];
    }

    const cartButton = document.querySelectorAll(".btn-my-cart__item-count")[0];

    // Update the cart item indicator outside of Vue

    if (data.totalCommerceItemCount > 0) {
      if (cartButton) {
        cartButton.innerHTML = data.totalCommerceItemCount;
      }
      document.querySelectorAll("body")[0].classList.add("has-items-in-cart");
    } else if (data.totalCommerceItemCount == 0) {
      if (cartButton) {
        cartButton.innerHTML = "";
      }
      document
        .querySelectorAll("body")[0]
        .classList.remove("has-items-in-cart");
    }

    // Price is updated here because tax/coupons/discounts is not calculated until checkout on sth/explore
    var orderTotalInCartIcon = document.querySelector(".header__order-total");

    if (orderTotalInCartIcon) {
      orderTotalInCartIcon.innerHTML = data.priceInfo.itemTotal.formattedAmount;
    }

    // Set the aria-label of the cart icon for accessibility
    var cartIcon = document.querySelector(".btn-my-cart");

    if (cartIcon) {
      var cartIconAriaLabel;

      if (data.totalCommerceItemCount === 0) {
        cartIconAriaLabel = "Your cart is empty.";
      } else if (data.totalCommerceItemCount === 1) {
        cartIconAriaLabel =
          "There is " +
          data.totalCommerceItemCount +
          " item in your Cart totaling " +
          data.priceInfo.itemTotal.formattedAmount;
      } else {
        cartIconAriaLabel =
          "There are " +
          data.totalCommerceItemCount +
          " items in your Cart totaling " +
          data.priceInfo.itemTotal.formattedAmount;
      }

      cartIcon.setAttribute("aria-label", cartIconAriaLabel);
    }

    if (data.gratuity && !data.gratuity.hasOwnProperty("percentage")) {
      data.gratuity.amount = data.gratuity.amount.toFixed(2);
    }

    // var to check if selecting 'change store' from the timeslot modal so you can return to it after selecting store
    data.changeStoreFromTimeSlotPicker = false;
    // var to check if selecting 'add new address' from the timeslot modal so you can return to it after adding an address
    data.displayTimeSlotPickerAfterCompletion = true;

    data.loaded = true;

    this._vm.$set(this.state, "cart", data);
  },
  DECREMENT_ITEM_QUANTITY(state, item) {
    item.quantity--;

    // TODO use _.debounce to limit this
    this.dispatch("cart/PUT_ITEM_QUANTITY", item);
  },
  INCREMENT_ITEM_QUANTITY(state, item) {
    if (item.quantity >= 99) {
      return;
    }

    item.quantity++;

    // TODO use _.debounce to limit this
    this.dispatch("cart/PUT_ITEM_QUANTITY", item);
  },
  EDIT_ORDER_NOTE(state, value) {
    // This is the frontend state. Saving the note to server is POST_ORDER_NOTE
    state.orderNote = value;
  },
  EDIT_DELIVERY_INSTRUCTIONS(state, { instructions, location }) {
    // This is the frontend state. Saving the note to server is POST_DELIVERY_INSTRUCTIONS
    state.deliveryInstructions = instructions;
    if (location !== undefined) {
      state.preferredDeliveryLocation = location;
    }
  },
  TOGGLE_LEAVE_AT_DOOR(state, value) {
    const locationStr = value ? "" : "leave-at-door"; // toggled location
    state.preferredDeliveryLocation = locationStr;

    this.dispatch("cart/POST_DELIVERY_INSTRUCTIONS", {
      instructions: state.deliveryInstructions, // instructions is required by API
      location: locationStr
    });
  },
  TOGGLE_ITEM_SUBSTITUTION(state, item) {
    var commerceItem =
      state.commerceItems[item.categoryIndex].items[item.itemIndex];
    commerceItem.allowSubstitution = !commerceItem.allowSubstitution;
    this.dispatch("cart/PUT_ITEM_SUBSTITUTION", commerceItem);
  },
  TOGGLE_CART_SUBSTITUTIONS(state) {
    state.substitutionsAllowAll = !state.substitutionsAllowAll;
    this.dispatch("cart/PUT_CART_SUBSTITUTION", state.substitutionsAllowAll);
  },
  SET_FRONTEND_GRATUITY(state, object) {
    // {type: 'AMOUNT', amount: value}

    // debugger
    // state.gratuity.amount = object.amount
    state.gratuity.formattedAmount = "$" + (object.amount * 1).toFixed(2);
    // state.gratuity.type = object.type
  },
  SET_COMMITTING_ORDER(state, bool) {
    state.isCommittingOrder = bool;
  },
  SET_FETCHING_CART(state, bool) {
    state.isFetchingCart = bool;
  },
  SET_FULL_CART(state, bool) {
    state.useFullCart = bool;
  },
  SET_PAY_AT_PICKUP_ORDER(state, bool) {
    state.payAtPickupOrder = bool;
  },
  SET_CHECKOUT_SUCCESS(state, bool) {
    state.orderCompleted = bool
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
